import { createFileRoute } from "@tanstack/react-router";
import type { LevelInfo } from "building-types/building/showroomTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import { downloadBlob } from "common/utils/downloadFile";
import { fetchWithTimeout, handleFetchError } from "common/utils/fetchHelpers";
import { formatBytes } from "gfx/MeshDataStats";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import type { GLTF } from "gfx/threejs/examples/jsm/loaders/GLTFLoader";
import GLTFBuildingViewer from "lib/components/gltf/GLTFBuildingViewer";
import type { GLTFBuildingReadyInfo, GLTFViewMode } from "lib/components/gltf/useGLTFBuildingInViewer";
import { getToken } from "lib/ctx";
import getPageTitle from "lib/utils/getPageTitle";
import type { JSX } from "react";
import { useCallback, useMemo, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import Button from "ui/buttons/Button";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import withAuthentication from "../../components/withAuthentication";
import styles from "./building-gltf-lambda.module.scss";

interface GLTFLambdaParams {
  studioDocId: string;
  compression: "none" | "quantize" | "draco" | "meshopt";
}

const compressionOptions = [
  { label: "None", value: "none" },
  { label: "Quantize", value: "quantize" },
  { label: "Draco", value: "draco" },
  { label: "Meshopt", value: "meshopt" },
];

const viewModeOptions = [
  { label: "Exterior", value: "exterior" },
  { label: "Interior", value: "interior" },
];

const REQUEST_TIMEOUT_SECONDS = 120;

function BuildingGLTFLambdaPage(): JSX.Element {
  const [params, setParams] = useState<GLTFLambdaParams>({
    studioDocId: "",
    compression: "none",
  });

  const [loading, setLoading] = useState(false);
  const [gltfData, setGltfData] = useState<{ base64: string; byteSize: number } | null>(null);
  const [gltf, setGltf] = useState<GLTF | null>(null);
  const [error, setError] = useState<string>("");
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [viewerReady, setViewerReady] = useState(false);

  // View mode state for interior/exterior views
  const [viewMode, setViewMode] = useState<GLTFViewMode>("exterior");
  const [selectedLevelId, setSelectedLevelId] = useState<string | null>(null);
  const [levels, setLevels] = useState<LevelInfo[]>([]);

  const generateButtonText = useMemo(() => {
    if (loading) {
      return "Generating...";
    }
    return "Generate GLTF";
  }, [loading]);

  const handleParamChange = useCallback(
    (key: keyof GLTFLambdaParams) => (value: string | number | boolean | null) => {
      setParams((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  /**
   * Handle when the GLTF viewer is ready with level info
   */
  const handleGLTFReady = useCallback((info: GLTFBuildingReadyInfo): void => {
    setLevels(info.levels);
    setSelectedLevelId(info.defaultLevelId);
    setViewMode("exterior");
    setViewerReady(true);
  }, []);

  /**
   * Handle view mode change
   */
  const handleViewModeChange = useCallback((newMode: string | number | boolean | null): void => {
    setViewMode(newMode as GLTFViewMode);
  }, []);

  /**
   * Handle level selection change
   */
  const handleLevelChange = useCallback((levelId: string | number | boolean | null): void => {
    setSelectedLevelId(levelId as string);
  }, []);

  const generateGLTF = useCallback(async () => {
    if (!params.studioDocId.trim()) {
      setError("Studio Doc ID is required");
      return;
    }

    setLoading(true);
    setError("");
    setGltfData(null);
    setGltf(null);
    setResponseTime(null);
    setViewerReady(false);
    setLevels([]);

    const startTime = Date.now();

    try {
      const response = await fetchWithTimeout(
        `${WEB_API_BASE_PATH}/building-gltf/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: (await getToken()) ?? "",
          },
          body: JSON.stringify({
            studioDocId: params.studioDocId,
            compression: params.compression,
          }),
        },
        REQUEST_TIMEOUT_SECONDS * 1000,
      );

      const endTime = Date.now();
      setResponseTime(endTime - startTime);

      // Note: fetchWithTimeout already throws on !response.ok, so we only get here on success
      const result = await response.json();

      if (result.gltfBase64) {
        setGltfData({
          base64: result.gltfBase64,
          byteSize: result.byteSize,
        });

        // Parse base64 to ArrayBuffer
        const binaryString = atob(result.gltfBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const arrayBuffer = bytes.buffer as ArrayBuffer;

        // Parse GLTF
        const assetLoader = THREEAssetLoader.instance;
        const parsedGltf = await assetLoader.parseGLTF(arrayBuffer);
        setGltf(parsedGltf);
      } else {
        throw new Error(result.error || "No GLTF data returned from service");
      }
    } catch (err) {
      // handleFetchError properly extracts error info from Response objects,
      // Error instances, strings, and generic error objects
      const errorDetails = await handleFetchError(err as Error | Response | string, "GLTF generation failed");
      setError(errorDetails.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  const downloadGLTF = useCallback(() => {
    if (!gltfData) {
      return;
    }

    const binaryString = atob(gltfData.base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: "model/gltf-binary" });
    downloadBlob(`building-${params.studioDocId}.glb`, blob);
  }, [gltfData, params.studioDocId]);

  const clearResults = useCallback(() => {
    setGltfData(null);
    setGltf(null);
    setError("");
    setResponseTime(null);
    setViewMode("exterior");
    setSelectedLevelId(null);
    setLevels([]);
    setViewerReady(false);
  }, []);

  return (
    <>
      <Head>
        <title>{getPageTitle("Building GLTF Lambda Test")}</title>
        <style>{`
          body {
            overflow: hidden;
          }
        `}</style>
      </Head>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Building GLTF Lambda Test</h1>
          <p>Test the server-side building GLTF generation lambda</p>
        </div>

        <div className={styles.controls}>
          <div className={styles.controlsGrid}>
            <div className={styles.inputGroup}>
              <label htmlFor="studioDocId">Studio Doc ID:</label>
              <Input
                id="studioDocId"
                type="text"
                value={params.studioDocId}
                onChange={(e) => handleParamChange("studioDocId")(e.currentTarget.value)}
                placeholder="Enter studio doc ID"
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="compression">Compression:</label>
              <Select
                value={params.compression}
                onChange={handleParamChange("compression")}
                options={compressionOptions}
              />
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <Button onClick={generateGLTF} disabled={loading}>
              {generateButtonText}
            </Button>
            <Button onClick={clearResults} disabled={!gltfData && !error}>
              Clear
            </Button>
          </div>
        </div>

        {error && (
          <div className={styles.error}>
            <h3>Error</h3>
            <p>The GLTF generation request failed with the following error:</p>
            <pre>{error}</pre>
          </div>
        )}

        {(responseTime !== null || gltfData || error) && (
          <div className={styles.stats}>
            {responseTime !== null && (
              <div className={styles.stat}>
                <strong>Response Time:</strong> {responseTime}ms
              </div>
            )}
            {gltfData && (
              <div className={styles.stat}>
                <strong>File Size:</strong> {formatBytes(gltfData.byteSize)}
              </div>
            )}
          </div>
        )}

        {gltfData && (
          <div className={styles.result}>
            <div className={styles.resultInfo}>
              <p>
                <strong>Status:</strong> GLTF generated successfully
              </p>
              <p>
                <strong>Studio Doc:</strong> {params.studioDocId}
              </p>
              <p>
                <strong>Compression:</strong> {params.compression}
              </p>
            </div>
            <Button className={styles.downloadButton} onClick={downloadGLTF}>
              Download GLB File
            </Button>
          </div>
        )}

        {viewerReady && levels.length > 0 && (
          <div className={styles.controls}>
            <div className={styles.controlsGrid}>
              <div className={styles.inputGroup}>
                <label htmlFor="viewMode">View Mode:</label>
                <Select value={viewMode} onChange={handleViewModeChange} options={viewModeOptions} />
              </div>

              {viewMode === "interior" && levels.length > 1 && (
                <div className={styles.inputGroup}>
                  <label htmlFor="levelSelect">Level:</label>
                  <Select
                    value={selectedLevelId ?? ""}
                    onChange={handleLevelChange}
                    options={levels.map((level) => ({
                      label: level.name,
                      value: level.id,
                    }))}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.comparisonContainer}>
          <div className={styles.viewerPane}>
            <h3>Lambda GLTF Output</h3>
            <div className={styles.viewerContent}>
              <GLTFBuildingViewer
                gltf={gltf}
                viewMode={viewMode}
                selectedLevelId={selectedLevelId}
                onReady={handleGLTFReady}
              />
              {!gltf && !loading && <div className={styles.placeholder}>Generate GLTF to view result</div>}
              {loading && <div className={styles.placeholder}>Loading...</div>}
            </div>
          </div>
        </div>

        <div className={styles.info}>
          <h3>Instructions:</h3>
          <ol>
            <li>Enter a valid Studio Doc ID</li>
            <li>Select compression type (Draco or Meshopt recommended for production)</li>
            <li>Click &quot;Generate GLTF&quot; to invoke the lambda</li>
            <li>View the result in the 3D viewer or download the GLB file</li>
          </ol>

          <h3>Compression Options:</h3>
          <ul>
            <li>
              <strong>None:</strong> Raw geometry data, largest file size
            </li>
            <li>
              <strong>Quantize:</strong> Reduces precision, smaller file, fast decode
            </li>
            <li>
              <strong>Draco:</strong> Best compression ratio, slower decode
            </li>
            <li>
              <strong>Meshopt:</strong> Good compression, fast decode for web
            </li>
          </ul>

          <h3>API Endpoint:</h3>
          <pre>{`POST /api/web/v1/building-gltf/generate
{
  "studioDocId": "string",
  "compression": "none" | "quantize" | "draco" | "meshopt"
}`}</pre>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/building-gltf-lambda")({
  component: withAuthentication(BuildingGLTFLambdaPage, { superAdmin: true }),
});
